import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { TrophyIcon } from '@heroicons/react/24/outline';
import { supabase } from '../lib/supabase';
import type { ContentItem } from '../types/content';

interface Champion {
  name: string;
  country: string;
  score: string;
  image: string;
  highlight: string;
  stats: {
    prize: string;
    participants: string;
    spectators: string;
    year?: string;
  };
}

function TournamentLegacy() {
  const [champions, setChampions] = useState<Champion[]>([
    {
      name: "Benjamin Ntuk",
      country: "Nigeria",
      score: "-1",
      image: "https://images.unsplash.com/photo-1535131749006-b7f58c99034b?w=800",
      highlight: "Record-breaking final round of 63",
      stats: {
        prize: "₦20M",
        participants: "144",
        spectators: "50,000",
        year: "2024"
      }
    },
    {
      name: "Esther Thompson",
      country: "Nigeria",
      score: "+1",
      image: "https://images.unsplash.com/photo-1622819584099-e04ccb14e8a7?w=800",
      highlight: "First female winner in tournament history",
      stats: {
        prize: "₦15M",
        participants: "72",
        spectators: "45,000",
        year: "2024"
      }
    }
  ]);
  const [sectionTitle, setSectionTitle] = useState("Tournament Legacy");
  const [sectionDescription, setDescription] = useState("Celebrating the champions who have made their mark in African golf history");
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    fetchContent();
  }, []);

  useEffect(() => {
    if (champions.length > 1) {
      const interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % champions.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [champions.length]);

  const safeParseJSON = (content: unknown): unknown => {
    if (typeof content !== 'string') return content;
    try {
      return JSON.parse(content);
    } catch {
      return content;
    }
  };

  const fetchContent = async () => {
    try {
      const { data: sections, error } = await supabase
        .from('content_sections')
        .select(`
          id,
          content_items (*)
        `)
        .eq('page', 'home')
        .eq('title', 'Tournament Legacy')
        .maybeSingle();

      if (error) throw error;

      if (sections?.content_items && sections.content_items.length > 0) {
        const titleItem = sections.content_items.find((item: ContentItem) =>
          item.title === 'Section Title' && item.status === 'Published'
        );

        const descriptionItem = sections.content_items.find((item: ContentItem) =>
          item.title === 'Description' && item.status === 'Published'
        );

        const championsItem = sections.content_items.find((item: ContentItem) =>
          item.title === 'Champions' && item.status === 'Published'
        );

        if (titleItem?.content) {
          const parsedTitle = safeParseJSON(titleItem.content);
          if (typeof parsedTitle === 'string') {
            setSectionTitle(parsedTitle);
          }
        }

        if (descriptionItem?.content) {
          const parsedDescription = safeParseJSON(descriptionItem.content);
          if (typeof parsedDescription === 'string') {
            setDescription(parsedDescription);
          }
        }

        if (championsItem?.content) {
          const parsedChampions = safeParseJSON(championsItem.content);
          if (Array.isArray(parsedChampions)) {
            setChampions(parsedChampions);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching content:', error);
    }
  };

  return (
    <section className="py-24 bg-white relative overflow-hidden">
      <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa')] bg-cover bg-fixed bg-center opacity-5" />

      <div className="container mx-auto px-4 relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-7xl mx-auto"
        >
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-golf-gray mb-6">
              {sectionTitle}
            </h2>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              {sectionDescription}
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center mb-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="relative aspect-[3/4] rounded-2xl overflow-hidden shadow-2xl max-w-md mx-auto">
                {champions.map((champion, index) => (
                  <div
                    key={index}
                    className={`absolute inset-0 transition-opacity duration-1000 ${
                      index === currentIndex ? 'opacity-100' : 'opacity-0'
                    }`}
                  >
                    <img
                      src={champion.image}
                      alt={`${champion.name} from ${champion.country}, ${champion.stats.year} champion with score ${champion.score}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.onerror = null;
                        target.src = "https://images.unsplash.com/photo-1535131749006-b7f58c99034b?w=800";
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent">
                      <div className="absolute bottom-0 left-0 p-8 w-full">
                        <div className="flex items-center gap-2 text-golf-orange font-medium mb-3">
                          <span>{champion.stats?.year || "2024"}</span>
                          <span className="w-1.5 h-1.5 rounded-full bg-golf-orange" />
                          <span>{champion.score}</span>
                        </div>
                        <h3 className="text-2xl md:text-3xl font-display font-bold text-white mb-2">{champion.name}</h3>
                        <p className="text-golf-sand mb-4">{champion.country}</p>
                        <p className="text-white/80 mb-6">{champion.highlight}</p>
                        <div className="grid grid-cols-3 gap-2 sm:gap-4">
                          {Object.entries(champion.stats).filter(([key]) => key !== 'year').map(([key, value]) => (
                            <div key={key} className="text-center p-2 bg-white/10 backdrop-blur-sm rounded-lg">
                              <div className="text-golf-orange font-bold">{value}</div>
                              <div className="text-white/60 text-sm capitalize">{key}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {champions.length > 1 && (
                <div className="flex justify-center gap-2 mt-4">
                  {champions.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentIndex(index)}
                      className={`w-2 h-2 rounded-full transition-all ${
                        index === currentIndex ? 'bg-golf-orange w-8' : 'bg-gray-300'
                      }`}
                      aria-label={`View champion ${index + 1}`}
                    />
                  ))}
                </div>
              )}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              <div className="bg-golf-sand/30 rounded-2xl p-8">
                <div className="flex items-start gap-4 mb-6">
                  <TrophyIcon className="w-8 h-8 text-golf-orange flex-shrink-0" />
                  <div>
                    <h3 className="text-xl md:text-2xl font-bold text-golf-gray mb-2">Tournament Highlights</h3>
                    <p className="text-gray-600">
                      The Ibom Open has become a cornerstone of African golf, attracting top talent
                      and setting new standards for excellence in the sport.
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-golf-orange rounded-full" />
                      <span className="text-gray-600">₦50M Total Prize Pool</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-golf-orange rounded-full" />
                      <span className="text-gray-600">15+ Countries Represented</span>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-golf-orange rounded-full" />
                      <span className="text-gray-600">50,000+ Spectators</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-golf-orange rounded-full" />
                      <span className="text-gray-600">Global Media Coverage</span>
                    </div>
                  </div>
                </div>
              </div>

              <Link
                to="/history"
                className="modern-button w-full py-4 text-center text-white text-lg font-medium inline-block"
              >
                View Tournament History
              </Link>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default TournamentLegacy;
